import { ColorScale, definePreset } from '@primeuix/themes';
import { Preset } from '@primeuix/themes/types';

import * as components from './components/index.js';
import { createSemanticPreset } from './semantics/index.js';
import * as a from './tokens/aliases.js';
import { type AliasSet, type SemanticOverrides, createSemanticTokens } from './tokens/semantic.js';

export type { SemanticOverrides } from './tokens/semantic.js';

/**
 * PrimeUI (PrimeNG 22+) commercial license token.
 *
 * Passed to `providePrimeNG({ license: PRIMENG_LICENSE })` in every app so the runtime license
 * check passes and the "Invalid PrimeUI License" banner is not shown. The token is verified
 * client-side (Ed25519) and ships in the browser bundle, so it is not a server secret.
 *
 */
export const PRIMENG_LICENSE =
  'eyJpZCI6IjBiMjdlMjljLTNjMzAtNDg0Zi1hNDkyLTczNGNlMmU4YzFmYSIsInByb2R1Y3QiOiJwcmltZXVpIiwidGllciI6ImNvbW11bml0eSIsInR5cGUiOiJkZXYiLCJpYXQiOjE3ODI5OTcyNzUsImV4cCI6MTgxNDUzMzI3NX0.4OU7XteT1MbCMRg2eqGUuG19XCjxX3PNKiQeB0nDBopUMcx1YrJps_PbSF11MRzv4GHYS1E7YL-bkU4nFjWaAw';

/**
 * Rentsody Design System Preset for PrimeNG
 *
 * Architecture:
 * - ALL tokens are defined STATICALLY at build time (no runtime CSS resolution)
 * - Primitives (primitives.ts) provide raw hex values from Figma
 * - Aliases (aliases.ts) map primitives to brand roles (primary, secondary, etc.)
 * - Semantic (semantic.ts) define Figma semantic tokens using '{reference}' syntax (reference layer)
 * - This preset maps tokens to PrimeNG's semantic structure (form, list, navigation, overlay)
 * - PrimeNG '{reference}' syntax links tokens together internally
 * - Component tokens are defined inline â€” no separate component files
 *
 * Token Hierarchy:
 * primitives.ts
 *
 * this preset (PrimeNG semantic mapping) CSS variables â†’ tailwindcss-primeui
 *
 * Note: semantic.ts serves as a Figma reference layer but isn't directly mapped 1:1
 * into this preset because PrimeNG uses a different semantic organization structure.
 *
 * Brand Switching:
 * Change aliases.ts to switch primary color palette. Everything updates on rebuild.
 */

export const RentsodyPreset = (
  primary: ColorScale = a.primary,
  secondary: ColorScale = a.secondary,
  surface: ColorScale = a.surface,
  success: ColorScale = a.success,
  warning: ColorScale = a.warning,
  danger: ColorScale = a.danger,
  semanticOverrides?: SemanticOverrides
): Preset => {
  // Merge caller overrides with complete defaults so AliasSet is always fully populated
  const al: AliasSet = {
    primary: { ...primary } as typeof a.primary,
    secondary: { ...a.secondary, ...secondary } as typeof a.secondary,
    surface: { ...a.surface, ...surface } as typeof a.surface,
    success: { ...a.success, ...success } as typeof a.success,
    warning: { ...a.warning, ...warning } as typeof a.warning,
    danger: { ...a.danger, ...danger } as typeof a.danger,
  };
  const s = createSemanticTokens(al, semanticOverrides);

  return definePreset({
    // Raw color palettes available via '{palette.shade}' references
    primitive: {
      primary: { ...primary },
      secondary: { ...secondary },
      success: { ...success },
      warning: { ...warning },
      danger: { ...danger },
      surface: { ...surface },

      // Sub-brand palettes
      lock: { ...a.lock },
      home: { ...a.home },
      guest: { ...a.guest },
      rentsody: { ...a.rentsody },
      rentals: { ...a.rentals },
    },

    // Fully dynamic — computed from alias set at call time
    semantic: createSemanticPreset(s, al),

    // All component configurations computed from alias set at call time
    components: {
      accordion: components.createAccordion(s),
      autocomplete: components.createAutocomplete(s),
      badge: components.createBadge(s),
      breadcrumb: components.createBreadcrumb(s),
      button: components.createButton(s),
      card: components.createCard(s),
      checkbox: components.createCheckbox(s),
      confirmdialog: components.createConfirmdialog(s),
      datatable: components.createDatatable(s),
      datepicker: components.createDatepicker(s),
      dialog: components.createDialog(s),
      iconfield: components.createIconfield(s),
      inputgroup: components.createInputgroup(s),
      inputnumber: components.createInputnumber(s),
      inputtext: components.createInputtext(s),
      menu: components.createMenu(s),
      message: components.createMessage(s),
      overlaybadge: components.createOverlaybadge(s),
      multiselect: components.createMultiselect(s),
      paginator: components.createPaginator(s),
      rating: components.createRating(s),
      select: components.createSelect(s),
      selectbutton: components.createSelectbutton(s),
      steps: components.createSteps(s),
      tabs: components.createTabs(s),
      tag: components.createTag(s),
      textarea: components.createTextarea(s),
      toast: components.createToast(s),
      togglebutton: components.createTogglebutton(s),
      toolbar: components.createToolbar(s),
      tooltip: components.createTooltip(s),
    },
  });
};

// Export alias for backward compatibility
export const createRentsodyPreset = RentsodyPreset;
